<template>
  <div class="home">
    <pc-header activeIndex="home"></pc-header>
    <div class="container pt-4">
      <div class="row mb-5 ">
        <div class="col-12 col-sm-6 ">

          <h1 class="mt-1 h1--home">
            PyCrunch - Continuous Test Runner for Python
          </h1>
          <div class="mb-4">It intelligently tracks which tests are affected by code change and automatically runs them.
          </div>

          <div class="text-secondary">
            Available as a plugin for PyCharm
          </div>
         <div class=" pt-2"> <a href="https://github.com/gleb-sevruk/pycrunch-intelij-plugin"
             class="text-blue-600 hover:text-blue-800 hover:underline">
            View plugin source code on GitHub
          </a>
         </div>
          <div class="get-started mt-5 d-flex align-items-center justify-content-center justify-content-md-start">
            <a href="#get-started" class="get-started__link ">
              <div class="get-started__button">
                Get Started
              </div>
            </a>
          </div>
          <div class="text-secondary ml-0 mt-2 ml-sm-0 mb-5 mb-sm-4 text-center text-sm-left">Works with Python 3.6+</div>

        </div>
        <div class="col-12 col-sm-6 ">
          <div class="video__container mb-4">
            <iframe
                class="youtube--iframe"
                width="910"
                height="540"
                src="https://www.youtube.com/embed/bjBzfP92PG8" frameborder="0"
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen></iframe>
          </div>
        </div>
      </div>
    </div>
    <div class="features-highlight">
      <div class="container">
        <div class="row">
          <div class="col-12">
            <h2 class="mt-4 pb-2">What's included?</h2>
          </div>
          <div class="col-12 col-sm-7 feature__col-image px-2 order-4 order-sm-0" >
            <img src="../assets/features/who_tests_what.png" class="img-fluid shadow feature__image ">

          </div>
          <div class="col-12 mt-4 mt-sm-0 col-sm-5 ">
            <h5>Understand What Covers What</h5>
            <p>Unlike traditional line coverage, PyCrunch tracks and records coverage for each test individually.</p>
            <p>Receive <strong>instant feedback</strong> with coverage markers as you work on your code. Stay informed about the impact of your changes.
</p>
          </div>
        </div>
        <div class="row mt-5">
          <div class="col-12 col-sm-7 feature__col-image px-2 order-4 order-sm-0">
            <img src="../assets/features/failing_test_path.png"
                 class="img-fluid shadow feature__image ">

          </div>
          <div class="col-12 col-sm-5 mt-4 mt-sm-0 ">
            <h5>Execution path of test failure</h5>
            <p>Markers are not only for coverage, and may have a different colors:</p>
            <div><span class="marker--green">Green</span> - A line covered by at least one successful test.</div>
            <div><span class="marker--red">Red</span> - At least one failing test hit this line.</div>
            <div><span class="marker--red">X</span> - Indicates the location where exceptions were recorded during test execution.</div>
            <p class="mt-3">Click on the coverage marker to see an overview of the tests that have failed on the current line.</p>
            <p>Identifying the specific code path that caused a test to fail makes it much easier to fix.</p>
          </div>
        </div>

        <div class="row mt-5">
          <div class="col-12 col-sm-7 feature__col-image px-2 order-4 order-sm-0">
            <img src="../assets/features/pytest_exception_stacktrace.png"
                 class="img-fluid shadow feature__image ">

          </div>
          <div class="col-12 col-sm-5 mt-4 mt-sm-0 ">
          <h5>Stack Trace and Locals at the Exception Point</h5>
            <p>Click on <span class="marker--red">X</span> to open the exception details popup.</p>
            <p class="mt-3">There, you will be able to see the stack trace and the state of local variables at the moment the exception occurred.</p>          </div>
        </div>
        <div class="row mt-5">
          <div class="col-12 col-sm-7 feature__col-image px-2 order-4 order-sm-0">
            <img src="../assets/features/exception_locals.png"
                 class="img-fluid shadow feature__image ">

          </div>
          <div class="col-12 col-sm-5 mt-4 mt-sm-0 ">
          <h5>Examine Local Variables Without a Debugger</h5>
            <p>No need to run the debugger again to find out the value of a variable near the exception point.</p>
            <p class="mt-3">You can view the state of local variables at the moment the exception occurred.</p>
            <p class="mt-3">Complex nesting (up to 3 levels deep) is supported.</p>
          </div>

        </div>
        <div class="row mt-5">
          <div class="col-12 col-sm-7 feature__col-image px-2 order-4 order-sm-0">
            <img src="../assets/features/python_variable_tracing.png"
                 class="img-fluid shadow feature__image ">

          </div>
          <div class="col-12 col-sm-5 mt-4 mt-sm-0 ">
            <h5> Application state tracing </h5>

            <p>Knowing execution path is not enough to understand the application under test.
              </p>
            <p>
              It is critically important to see the state of an application at any point in time.</p>
            <p> Now, you can use <strong>trace</strong>
              <router-link to="/docs/variables-tracing">
                function from PyCrunch
              </router-link> to see variable values at any point of execution.</p>
          </div>
        </div>
          <div class="row mt-5">
            <div class="col-12 col-sm-7 feature__col-image px-2 order-4 order-sm-0">
              <img src="../assets/features/python_test_output_interception.png"
                   class="img-fluid shadow feature__image ">

            </div>
            <div class="col-12 col-sm-5 mt-4 mt-sm-0 ">
              <h5>Output interception</h5>
              <p>All of your program's output is recorded for each test.</p>
              <p>Sometimes you start debugging only to see what is the value of some local variable. Now there is no
                need to.</p>
              <p> Just add some logging, and it will appear in test <strong>output window</strong> immediately.</p>
            </div>
          </div>
          <div class="row mt-5">
            <div class="col-12 col-sm-7 feature__col-image px-2 order-4 order-sm-0">
              <img src="../assets/features/navigate_to_test.png"
                   class="img-fluid shadow feature__image ">

            </div>
            <div class="col-12 col-sm-5 mt-4 mt-sm-0 ">
              <h5>Easy Navigation</h5>
              <p>Going back to affected test from code line might be tricky.</p>
              <p>Now, you can instantly navigate back to test from the coverage marker popup or by double-clicking on the test in the <strong>test list</strong>.</p>
              <p class="mt-3">In case a test fails, you can jump directly to the recorded exception point.</p>
            </div>
          </div>
          <div class="row mt-5 pb-5">
            <div class="col-12 col-sm-7 feature__col-image px-2 order-4 order-sm-0">
              <img src="../assets/features/python_continuous_test_engine_mode.png"
                   class="img-fluid shadow feature__image ">

            </div>
            <div class="col-12 col-sm-5 mt-4 mt-sm-0 ">
              <h5>Automatic Testing</h5>
              <p>Usual approach is to write code, then stop, and run test.</p>
              <p>With PyCrunch you will unlock full power of Test-Driven Development. There is no more need to stop writing code. All your tests will be executed in background automatically.</p>
              <p>Because engine keeps track of dependencies between code and tests, this allows to run <strong>only impacted</strong> tests.</p>
              <p>If this is not a desired behaviour, you can always switch <strong>engine mode</strong> to manual, or pin some of the tests and choose to only run <strong>pinned tests</strong>.</p>
            </div>
          </div>
        <div class="row pb-4">
          <div class="col-12">
            <h5>Other features</h5>
            <ul>
              <li>Django Support</li>
              <li>Support running in the Docker container</li>
            </ul>
          </div>
        </div>
        </div>
      </div>
    <a name="get-started"></a>

    <pc-quick-start />
    <pc-pytrace-promo override-title="If you like PyCrunch, you might be also interested in our new product:"/>
    <pc-footer/>
  </div>

</template>

<script>
  // @ is an alias to /src
  import PcHeader from '@/components/pc-header.component'
  import PcFooter from '../views/PcFooter'
  import PcQuickStart from '../views/documentation/quick-start.component'
  import PcPytracePromo from './PcPytracePromo'

  export default {
    name: 'home',
    data () {
      return {

      }
    },
    components: {
      PcPytracePromo,
      PcQuickStart,
      PcFooter,
      PcHeader,

    },
    methods: {
    },
  }
</script>
<style scoped>
  .h1--home {
    font-size: 1.3em;
  }

  .get-started__button {
    background: linear-gradient(123.71deg, #4E9B00 2.01%, #019C23 100%);
    box-shadow: -1px 1px 6px rgba(9, 157, 32, 0.22);
    /*box-shadow: -1px 1px px rgba(138, 150, 162, 0.8);*/
    border-radius: 4px;
    padding: .8em;
    width: 200px;
    text-align: center;
    font-style: normal;
    font-weight: 500;
    color: #FFFFFF;
    cursor: pointer;
  }

  .get-started__button:hover {
    background: linear-gradient(123.71deg, #448700 2.01%, #019C23 100%);
    box-shadow: -1px 2px 10px rgba(9, 157, 32, 0.40);

  }

  .features-highlight {
    background-color: #eaeaec;
  }

  .feature__image {
    /*max-width: 810px;*/
  }

  .video__container {
    position: relative;
    padding-bottom: 57.35%; /* 16:9 */
    padding-top: 25px;
    height: 0;
  }

  .video__container .youtube--iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
  .marker--green {
    color: #46673D;
    font-weight: 600;
  }
  .marker--red {
    color: #BE0000;
    font-weight: 600;
  }
  .get-started__link {
    text-decoration: none;
  }
</style>
